import { useMemo } from 'react';
import { Alert, Button, PaidIcon, Spinner } from '@billda/design-system';
import { useMarkPotPaid } from '../../hooks/useMarkPotPaid';
import { PotSummaryCard } from '../PotSummaryCard';
import {
  PaymentBreakdownCard,
  type PaymentLineItem,
} from '../PaymentBreakdownCard';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface MarkPotPaidPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
}

export function MarkPotPaidPanelContent({
  potDetails,
  onSuccess,
}: MarkPotPaidPanelContentProps) {
  const { mutate: markPotPaid, isPending } = useMarkPotPaid();

  const handleConfirmPayment = () => {
    markPotPaid(
      {
        potPeriodId: potDetails.potPeriodId,
        potId: potDetails.id,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  // Build payment line items from gang member splits, fixed rate workers, or solo owner
  const paymentLineItems = useMemo((): PaymentLineItem[] => {
    if (potDetails.owner.ownerType === 'Solo') {
      // Solo pot: single line item for the owner
      return [
        {
          id: 'owner',
          name: potDetails.owner.ownerName,
          amount: potDetails.financials?.netAmount ?? 0,
          type: 'solo',
        },
      ];
    }

    // Gang pot: combine gang member splits and fixed rate workers
    const items: PaymentLineItem[] = [];

    // Add gang member splits (only those with non-zero amounts)
    // Fixed rate workers have £0 split and are shown separately
    potDetails.gangMemberSplits
      .filter(member => member.amount > 0)
      .forEach(member => {
        items.push({
          id: `split-${member.userId}`,
          name: member.userName,
          amount: member.amount,
          type: 'split',
        });
      });

    // Add fixed rate workers
    potDetails.fixedRateWorkers.forEach(worker => {
      items.push({
        id: `dayrate-${worker.userId}`,
        name: worker.userName,
        amount: worker.totalAmount,
        type: 'dayRate',
      });
    });

    return items;
  }, [potDetails]);

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-6">
          <PotSummaryCard
            ownerType={potDetails.owner.ownerType}
            ownerName={potDetails.owner.ownerName}
            period={potDetails.period}
          />

          <PaymentBreakdownCard items={paymentLineItems} />
        </div>
      </div>

      {/* Fixed bottom action area */}
      <div className="border-t border-border-default bg-surface-default p-6">
        {/* Helper text */}
        <Alert severity="info" className="mb-4">
          Marking this pot as paid confirms the payment has been processed. This
          action cannot be undone.
        </Alert>

        <Button
          variant="success"
          size="lg"
          fullWidth
          onClick={handleConfirmPayment}
          disabled={isPending}
          data-testid="confirm-payment-button"
        >
          {isPending ? (
            <Spinner size={20} color="inherit" />
          ) : (
            <PaidIcon aria-hidden="true" className="size-5" />
          )}
          {isPending ? 'Processing...' : 'Confirm Payment'}
        </Button>
      </div>
    </div>
  );
}
